import {FC, useEffect, useState} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Button} from '../../../components/Button/Button'
import {Input} from '../../../components/Input/Input'
import Swal from 'sweetalert2'
import {DELETE, GET, POST, PUT} from '../../../services/api'
import {Loading} from '../../../components/Loading/Loading'
import {useNavigate, useParams} from 'react-router-dom'
import {i18n} from '../../../translate/i18n'
import {BreadCrumbs} from '../../../components/BreadCrumbs/breadCrumbrs'
import uuid from 'react-uuid'
import {TourDifficultyEnum, getDifficultyLabel, TOUR_INCLUDES_OPTIONS, DAYS_OF_WEEK} from './types'
import {HospitalityTabs} from '../../../components/HospitalityTabs/HospitalityTabs'
import {EditorQuill} from '../../../components/EditorQuill/EditorQuill'
import {TitleFreePriceInput} from '../Comuns'

const EditHotelTourContent: FC = () => {
  const navigate = useNavigate()
  const {id} = useParams()
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('basic') // Tab control
  const [isLoading, setIsLoading] = useState(false)
  const [tourIncludes, setTourIncludes] = useState<string[]>([])
  const [tourSchedules, setTourSchedules] = useState<
    {
      startTime: string
      daysOfWeek: number[]
      seasonalStart: string
      seasonalEnd: string
      status: boolean
    }[]
  >([])
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    duration: '',
    difficulty: TourDifficultyEnum.EASY,
    price: '',
    currency: 'BRL',
    rating: '0',
    titleFreePrice: null as string | null,
    status: true,
    requiresReservation: true,
    maxParticipants: '',
    minParticipants: '1',
    ageRestriction: '',
    physicalRequirements: '',
    weatherDependent: false,
    cancellationHours: '24',
    discountChildren: '0',
    includesTransfer: false,
  })
  const [image, setImage] = useState<File | string | null>(null)
  const [firstStateImage, setFirstStateImage] = useState<File | string | null>(null)

  // Helper functions for managing tour schedules
  const addSchedule = () => {
    setTourSchedules([
      ...tourSchedules,
      {
        startTime: '',
        daysOfWeek: [],
        seasonalStart: '',
        seasonalEnd: '',
        status: true,
      },
    ])
  }

  const updateSchedule = (index: number, field: string, value: string | number[] | boolean) => {
    const updated = [...tourSchedules]
    updated[index] = {...updated[index], [field]: value}
    setTourSchedules(updated)
  }

  const removeSchedule = (index: number) => {
    setTourSchedules(tourSchedules.filter((_, i) => i !== index))
  }

  // Helper functions for managing tour includes
  const toggleInclude = (include: string) => {
    if (tourIncludes.includes(include)) {
      setTourIncludes(tourIncludes.filter((item) => item !== include))
    } else {
      setTourIncludes([...tourIncludes, include])
    }
  }

  const handleInputChange = (
    field: string,
    value: string | TourDifficultyEnum | boolean | number
  ) => {
    setFormData((prev) => {
      const updated = {
        ...prev,
        [field]: value,
      } as any

      // Se o campo alterado for 'price', limpar titleFreePrice quando preço for maior que 0
      if (field === 'price') {
        const num = Number(String(value))
        if (Number.isFinite(num) && num > 0) {
          updated.titleFreePrice = ''
        }
      }

      return updated
    })
  }

  const validateForm = () => {
    if (!formData.title.trim()) {
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Título é obrigatório!',
      })
      return false
    }

    if (formData.title.length > 255) {
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Título deve ter no máximo 255 caracteres!',
      })
      return false
    }

    if (formData.duration && formData.duration.length > 100) {
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Duração deve ter no máximo 100 caracteres!',
      })
      return false
    }

    if (formData.price && parseFloat(formData.price) < 0) {
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Preço deve ser maior ou igual a zero!',
      })
      return false
    }

    if (formData.rating && (parseFloat(formData.rating) < 0 || parseFloat(formData.rating) > 5)) {
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Avaliação deve estar entre 0 e 5!',
      })
      return false
    }

    if (
      formData.maxParticipants &&
      parseInt(formData.maxParticipants) < parseInt(formData.minParticipants)
    ) {
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Número máximo de participantes deve ser maior que o mínimo!',
      })
      return false
    }

    if (
      formData.discountChildren &&
      (parseFloat(formData.discountChildren) < 0 || parseFloat(formData.discountChildren) > 100)
    ) {
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Desconto para crianças deve estar entre 0 e 100%!',
      })
      return false
    }

    return true
  }

  const getTour = async () => {
    try {
      setLoading(true)
      const response = await GET(`/hotel-tours/${id}`)

      if (response.success) {
        const tour = response.data
        setFormData({
          title: tour.title || '',
          description: tour.description || '',
          duration: tour.duration || '',
          difficulty: tour.difficulty || TourDifficultyEnum.EASY,
          price: tour.price ? tour.price.toString() : '',
          currency: tour.currency || 'BRL',
          rating: tour.rating ? tour.rating.toString() : '0',
          titleFreePrice: tour.titleFreePrice || '',
          status: tour.status !== undefined ? tour.status : true,
          requiresReservation:
            tour.requiresReservation !== undefined ? tour.requiresReservation : true,
          maxParticipants: tour.maxParticipants ? tour.maxParticipants.toString() : '',
          minParticipants: tour.minParticipants ? tour.minParticipants.toString() : '1',
          ageRestriction: tour.ageRestriction || '',
          physicalRequirements: tour.physicalRequirements || '',
          weatherDependent: tour.weatherDependent !== undefined ? tour.weatherDependent : false,
          cancellationHours: tour.cancellationHours ? tour.cancellationHours.toString() : '24',
          discountChildren: tour.discountChildren ? tour.discountChildren.toString() : '0',
          includesTransfer: tour.includesTransfer !== undefined ? tour.includesTransfer : false,
        })

        if (tour.image) {
          setImage(tour.image)
          setFirstStateImage(tour.image)
        }

        // Load tour includes from tour.tourIncludes if present
        if (Array.isArray(tour.tourIncludes)) {
          setTourIncludes(tour.tourIncludes.map((item: any) => item.item))
        } else {
          // fallback to API if not present
          try {
            const includesResponse = await GET(`/tour-includes/tour/${id}`)
            if (includesResponse.success && includesResponse.data) {
              const includes = includesResponse.data.map((item: any) => item.item)
              setTourIncludes(includes)
            }
          } catch (error) {
            console.log('Error loading tour includes:', error)
          }
        }

        // Load tour schedules from tour.tourSchedules if present
        if (Array.isArray(tour.tourSchedules)) {
          const schedules = tour.tourSchedules.map((schedule: any) => ({
            startTime: schedule.startTime || '',
            daysOfWeek: Array.isArray(schedule.daysOfWeek) ? schedule.daysOfWeek : [],
            seasonalStart: schedule.seasonalStart ? schedule.seasonalStart.split('T')[0] : '',
            seasonalEnd: schedule.seasonalEnd ? schedule.seasonalEnd.split('T')[0] : '',
            status: schedule.status !== undefined ? schedule.status : true,
          }))
          setTourSchedules(schedules)
        } else {
          // fallback to API if not present
          try {
            const schedulesResponse = await GET(`/tour-schedules/tour/${id}`)
            if (schedulesResponse.success && schedulesResponse.data) {
              const schedules = schedulesResponse.data.map((item: any) => ({
                startTime: item.startTime || '',
                daysOfWeek: Array.isArray(item.daysOfWeek) ? item.daysOfWeek : [],
                seasonalStart: item.seasonalStart ? item.seasonalStart.split('T')[0] : '',
                seasonalEnd: item.seasonalEnd ? item.seasonalEnd.split('T')[0] : '',
                status: item.status !== undefined ? item.status : true,
              }))
              setTourSchedules(schedules)
            }
          } catch (error) {
            console.log('Error loading tour schedules:', error)
          }
        }
      }

      setLoading(false)
    } catch (e: any) {
      setLoading(false)
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: e.message || 'Erro ao carregar o tour!',
      })
      console.log(e)
    }
  }

  function validateTourSchedules() {
    if (tourSchedules.length === 0) {
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Horários são obrigatórios!',
      })
      return false
    }

    if (tourSchedules.find((e) => e.daysOfWeek.length === 0)) {
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Dias são obrigatórios nos horários!',
      })
      return false
    }

    for (let index = 0; index < tourSchedules.length; index++) {
      const schedule = tourSchedules[index]
      if (!schedule.daysOfWeek || schedule.daysOfWeek.length === 0) {
        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: `Selecione pelo menos um dia da semana para o horário #${index + 1}.`,
        })
        return false
      }
      if (!schedule.startTime) {
        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: `Informe o horário de início para o horário #${index + 1}.`,
        })
        return false
      }
    }

    return true
  }

  const updateTour = async () => {
    if (!validateForm()) return

    if (!validateTourSchedules()) return

    try {
      setLoading(true)

      let imageRes = {
        success: false,
        image: firstStateImage,
      }

      // Upload da imagem se foi alterada
      if (image !== firstStateImage && image instanceof File) {
        if (firstStateImage) {
          await POST('/upload-images/delete-one', {
            url: firstStateImage,
          })
        }

        const formData = new FormData()
        formData.append('file', image)
        formData.append('nameImage', `publicity-${uuid()}.jpg`)
        formData.append('originalSize', 'true')
        imageRes = await POST('/upload-images/upload-one', formData)
      }

      const payload = {
        title: formData.title.trim(),
        description: formData.description.trim() || undefined,
        duration: formData.duration.trim() || undefined,
        difficulty: formData.difficulty,
        price: formData.price ? parseFloat(formData.price) : null,
        currency: formData.currency,
        rating: parseFloat(formData.rating),
        image: imageRes.success ? imageRes.image : typeof image === 'string' ? image : undefined,
        status: formData.status,
        requiresReservation: formData.requiresReservation,
        maxParticipants: formData.maxParticipants ? parseInt(formData.maxParticipants) : undefined,
        minParticipants: parseInt(formData.minParticipants),
        ageRestriction: formData.ageRestriction.trim() || undefined,
        physicalRequirements: formData.physicalRequirements.trim() || undefined,
        weatherDependent: formData.weatherDependent,
        cancellationHours: parseInt(formData.cancellationHours),
        discountChildren: parseFloat(formData.discountChildren),
        includesTransfer: formData.includesTransfer,
        titleFreePrice: formData.titleFreePrice?.trim() || null,
      }

      const response = await PUT(`/hotel-tours/${id}`, payload)

      if (response.success) {
        // Remove existing tour includes and schedules
        try {
          await DELETE(`/tour-includes/tour/${id}`)
        } catch (error) {
          console.log('Error removing existing data:', error)
        }

        // Add tour includes
        if (tourIncludes.length > 0) {
          const incluirTours = tourIncludes.map((include) => {
            return {
              tourId: response.data.id,
              item: include,
            }
          })
          await POST('/tour-includes/multiples', incluirTours)
        }

        // Add tour schedules
        if (tourSchedules.length > 0) {
          const schedulesCreate = tourSchedules
            .filter((schedule) => schedule.daysOfWeek.length > 0 && schedule.startTime)
            .map((schedule) => {
              return {
                tourId: response.data.id,
                startTime: schedule.startTime,
                daysOfWeek: schedule.daysOfWeek,
                seasonalStart: schedule.seasonalStart || null,
                seasonalEnd: schedule.seasonalEnd || null,
                status: schedule.status,
              }
            })
          await POST('/tour-schedules/multiples', schedulesCreate)
        }

        Swal.fire({
          icon: 'success',
          title: String(i18n.t('hotel_tour.btn_edit')),
          text: String(i18n.t('hotel_tour.msg_success_update')),
        }).then(() => {
          navigate('/hospitality/tours')
        })
      } else {
        throw new Error(response.message || 'Erro ao atualizar tour')
      }

      setLoading(false)
    } catch (e: any) {
      setLoading(false)
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('hotel_tour.msg_error_update')),
        text: String(e.message || i18n.t('hotel_tour.msg_error_update')),
      })
      console.log(e)
    }
  }

  useEffect(() => {
    if (id) {
      getTour()
    }
  }, [id])

  const renderBasicTab = () => (
    <div className='tab-content'>
      <div className='row'>
        <div className='col-md-6'>
          <Input
            inputAttr={{
              type: 'default',
            }}
            label={i18n.t('hotel_tour.field_title')}
            placeholder={i18n.t('hotel_tour.field_title_placeholder')}
            maxCaracteres={255}
            value={formData.title}
            change={(value: string) => handleInputChange('title', value)}
            smalltext={i18n.t('hotel_tour.field_title_hint')}
          />
        </div>
        <div className='col-md-6'>
          <Input
            inputAttr={{
              type: 'default',
            }}
            label={i18n.t('hotel_tour.field_duration')}
            placeholder={i18n.t('hotel_tour.field_duration_placeholder')}
            maxCaracteres={100}
            value={formData.duration}
            change={(value: string) => handleInputChange('duration', value)}
            smalltext={i18n.t('hotel_tour.field_duration_hint')}
          />
        </div>
      </div>

      <div className='row'>
        <div className='col-12'>
          <EditorQuill
            value={formData.description}
            label={i18n.t('hotel_tour.field_description')}
            placeholder={i18n.t('hotel_tour.field_description_placeholder')}
            change={(value: string) => {
              setFormData((prev) => ({
                ...prev,
                description: value,
              }))
            }}
            maxLength={5000} // Limite de 5000 caracteres
          />
        </div>
      </div>

      <div className='row'>
        <div className='col-md-3'>
          <Input
            inputAttr={{
              type: 'select',
            }}
            label={i18n.t('hotel_tour.field_difficulty')}
            options={Object.values(TourDifficultyEnum).map((difficulty) => ({
              value: difficulty,
              select: getDifficultyLabel(difficulty),
            }))}
            value={formData.difficulty}
            change={(value: TourDifficultyEnum) => handleInputChange('difficulty', value)}
          />
        </div>
        <div className='col-md-3'>
          <Input
            inputAttr={{
              type: 'number',
              step: '0.01',
              min: '0',
            }}
            label={i18n.t('hotel_tour.field_price')}
            placeholder={i18n.t('hotel_tour.field_price_placeholder')}
            value={formData.price}
            change={(value: string) => handleInputChange('price', value)}
            smalltext={i18n.t('hotel_tour.field_price_hint')}
          />
        </div>
        <div className='col-md-3'>
          <Input
            inputAttr={{
              type: 'default',
            }}
            label={i18n.t('hotel_tour.field_currency')}
            placeholder='BRL'
            value={formData.currency}
            change={(value: string) => handleInputChange('currency', value)}
            smalltext={i18n.t('hotel_tour.field_currency_hint')}
          />
        </div>
        <div className='col-md-3'>
          <Input
            inputAttr={{
              type: 'number',
              step: '0.1',
              min: '0',
              max: '5',
            }}
            label={i18n.t('hotel_tour.field_rating')}
            placeholder='4.5'
            value={formData.rating}
            change={(value: string) => handleInputChange('rating', value)}
            smalltext={i18n.t('hotel_tour.field_rating_hint')}
          />
        </div>
      </div>

      <div className='row'>
        <div className='col-md-6'>
          <TitleFreePriceInput
            value={formData.titleFreePrice}
            onChange={(value) => handleInputChange('titleFreePrice', value || '')}
            label='Título Para Valor Sem Custo'
            placeholder='Ex:. Cortesia do Hotel'
            maxCaracteres={30}
            disabled={parseFloat(formData.price) !== 0 && formData.price !== ''}
            toolTip={true}
            toolTiptext='Se o preço estiver zerado, este texto poderá ser exibido no WebApp. Se vazio, nada será mostrado.'
            toolTipIcon='info-circle'
          />
        </div>
      </div>
    </div>
  )

  const renderDetailsTab = () => (
    <div className='tab-content'>
      <div className='row'>
        <div className='col-md-6'>
          <Input
            inputAttr={{
              type: 'number',
              min: '1',
            }}
            label={i18n.t('hotel_tour.field_participants_min')}
            placeholder='1'
            value={formData.minParticipants}
            change={(value: string) => handleInputChange('minParticipants', value)}
            smalltext={i18n.t('hotel_tour.field_participants_min_hint')}
          />
        </div>
        <div className='col-md-6'>
          <Input
            inputAttr={{
              type: 'number',
              min: '1',
            }}
            label={i18n.t('hotel_tour.field_participants_max')}
            placeholder={i18n.t('hotel_tour.field_participants_max_placeholder')}
            value={formData.maxParticipants}
            change={(value: string) => handleInputChange('maxParticipants', value)}
            smalltext={i18n.t('hotel_tour.field_participants_max_hint')}
          />
        </div>
      </div>

      <div className='row'>
        <div className='col-md-6'>
          <Input
            inputAttr={{
              type: 'default',
            }}
            label={i18n.t('hotel_tour.field_age_restriction')}
            placeholder={i18n.t('hotel_tour.field_age_restriction_placeholder')}
            value={formData.ageRestriction}
            change={(value: string) => handleInputChange('ageRestriction', value)}
            smalltext={i18n.t('hotel_tour.field_age_restriction_hint')}
          />
        </div>
        <div className='col-md-6'>
          <Input
            inputAttr={{
              type: 'default',
            }}
            label={i18n.t('hotel_tour.field_physical_requirements')}
            placeholder={i18n.t('hotel_tour.field_physical_requirements_placeholder')}
            value={formData.physicalRequirements}
            change={(value: string) => handleInputChange('physicalRequirements', value)}
            smalltext={i18n.t('hotel_tour.field_physical_requirements_hint')}
          />
        </div>
      </div>

      <div className='row'>
        <div className='col-md-4'>
          <Input
            inputAttr={{
              type: 'number',
              min: '1',
            }}
            label={i18n.t('hotel_tour.field_cancellation_hours')}
            placeholder='24'
            value={formData.cancellationHours}
            change={(value: string) => handleInputChange('cancellationHours', value)}
            smalltext={i18n.t('hotel_tour.field_cancellation_hours_hint')}
          />
        </div>
        <div className='col-md-4'>
          <Input
            inputAttr={{
              type: 'number',
              step: '0.01',
              min: '0',
              max: '100',
            }}
            label={i18n.t('hotel_tour.field_discount_children')}
            placeholder='0'
            value={formData.discountChildren}
            change={(value: string) => handleInputChange('discountChildren', value)}
            smalltext={i18n.t('hotel_tour.field_discount_children_hint')}
          />
        </div>
      </div>

      <div className='row'>
        <div className='col-md-3'>
          <Input
            inputAttr={{
              type: 'checkbox',
            }}
            label={i18n.t('hotel_tour.field_requires_reservation')}
            checkSwift={true}
            checked={formData.requiresReservation}
            change={(checked: boolean) => handleInputChange('requiresReservation', checked)}
            smalltext={i18n.t('hotel_tour.field_requires_reservation_hint')}
          />
        </div>
        <div className='col-md-3'>
          <Input
            inputAttr={{
              type: 'checkbox',
            }}
            label={i18n.t('hotel_tour.field_transfer')}
            checkSwift={true}
            checked={formData.includesTransfer}
            change={(checked: boolean) => handleInputChange('includesTransfer', checked)}
            smalltext={i18n.t('hotel_tour.field_transfer_hint')}
          />
        </div>
        <div className='col-md-3'>
          <Input
            inputAttr={{
              type: 'checkbox',
            }}
            label={i18n.t('hotel_tour.field_weather_dependent')}
            checkSwift={true}
            checked={formData.weatherDependent}
            change={(checked: boolean) => handleInputChange('weatherDependent', checked)}
            smalltext={i18n.t('hotel_tour.field_weather_dependent_hint')}
          />
        </div>
        <div className='col-md-3'>
          <Input
            inputAttr={{
              type: 'checkbox',
            }}
            label={i18n.t('hotel_tour.field_is_active')}
            checkSwift={true}
            checked={formData.status}
            change={(checked: boolean) => handleInputChange('status', checked)}
            smalltext={i18n.t('hotel_tour.field_is_active_hint')}
          />
        </div>
      </div>
    </div>
  )

  const renderSchedulesTab = () => (
    <div className='tab-content'>
      <div className='d-flex justify-content-between align-items-center mb-4'>
        <h5 className='mb-0'>{i18n.t('hotel_tour.tab_schedules')}</h5>
        <button type='button' className='btn btn-sm btn-primary' onClick={addSchedule}>
          <i className='fas fa-plus me-2'></i>
          {i18n.t('hotel_tour.add_schedule')}
        </button>
      </div>
      {tourSchedules.map((schedule, index) => (
        <div key={index} className='mb-4 p-4 border rounded'>
          <div className='row mb-3'>
            <div className='col-md-4'>
              <label className='form-label fw-bold'>
                {i18n.t('hotel_tour.schedule_start_time')} *
              </label>
              <input
                type='time'
                className='form-control'
                value={schedule.startTime}
                onChange={(e) => updateSchedule(index, 'startTime', e.target.value)}
              />
            </div>
            <div className='col-md-6'>
              <label className='form-label fw-bold'>
                {i18n.t('hotel_tour.schedule_days_of_week')} *
              </label>
              <div className='d-flex flex-wrap gap-2 mt-2'>
                {[
                  {value: 0, label: 'Dom'},
                  {value: 1, label: 'Seg'},
                  {value: 2, label: 'Ter'},
                  {value: 3, label: 'Qua'},
                  {value: 4, label: 'Qui'},
                  {value: 5, label: 'Sex'},
                  {value: 6, label: 'Sab'},
                ].map((day) => (
                  <label key={day.value} className='form-check d-flex align-items-center'>
                    <input
                      type='checkbox'
                      className='form-check-input me-2'
                      checked={schedule.daysOfWeek.includes(day.value)}
                      onChange={(e) => {
                        const newDays = e.target.checked
                          ? [...schedule.daysOfWeek, day.value].sort()
                          : schedule.daysOfWeek.filter((d) => d !== day.value)
                        updateSchedule(index, 'daysOfWeek', newDays)
                      }}
                    />
                    <span className='fw-bold'>{day.label}</span>
                  </label>
                ))}
              </div>
              {schedule.daysOfWeek.length > 0 && (
                <small className='text-muted'>
                  {i18n.t('hotel_tour.selected')}:{' '}
                  {schedule.daysOfWeek
                    .map((day) => ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'][day])
                    .join(', ')}
                </small>
              )}
            </div>
            <div className='col-md-2 d-flex align-items-end'>
              <button
                type='button'
                className='btn btn-sm btn-danger'
                onClick={() => removeSchedule(index)}
                title={i18n.t('hotel_tour.remove_schedule')}
              >
                <i className='fas fa-trash'></i>
              </button>
            </div>
          </div>
          <div className='row'>
            <div className='col-md-4'>
              <label className='form-label'>{i18n.t('hotel_tour.schedule_seasonal_start')}</label>
              <input
                type='date'
                className='form-control'
                value={schedule.seasonalStart}
                onChange={(e) => updateSchedule(index, 'seasonalStart', e.target.value)}
              />
              <small className='text-muted'>{i18n.t('hotel_tour.schedule_seasonal_hint')}</small>
            </div>
            <div className='col-md-4'>
              <label className='form-label'>{i18n.t('hotel_tour.schedule_seasonal_end')}</label>
              <input
                type='date'
                className='form-control'
                value={schedule.seasonalEnd}
                onChange={(e) => updateSchedule(index, 'seasonalEnd', e.target.value)}
              />
              <small className='text-muted'>{i18n.t('hotel_tour.schedule_seasonal_hint')}</small>
            </div>
            <div className='col-md-4'>
              <label className='form-label'>{i18n.t('hotel_tour.schedule_status')}</label>
              <div className='form-check form-switch'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  checked={schedule.status}
                  onChange={(e) => updateSchedule(index, 'status', e.target.checked)}
                />
                <label className='form-check-label'>{i18n.t('hotel_tour.field_is_active')}</label>
              </div>
            </div>
          </div>
        </div>
      ))}
      {tourSchedules.length === 0 && (
        <div className='text-center py-5 text-muted'>
          <i className='fas fa-clock fa-3x mb-3'></i>
          <h6>{i18n.t('hotel_tour.no_schedule_added')}</h6>
          <p>{i18n.t('hotel_tour.click_add_schedule')}</p>
        </div>
      )}
    </div>
  )

  const renderIncludesTab = () => (
    <div className='tab-content'>
      <h5 className='mb-4'>{i18n.t('hotel_tour.includes_title')}</h5>
      <div className='row'>
        {TOUR_INCLUDES_OPTIONS.map((option, index) => (
          <div key={index} className='col-md-4 mb-3'>
            <div className='form-check'>
              <input
                className='form-check-input'
                type='checkbox'
                id={`include-${index}`}
                checked={tourIncludes.includes(option)}
                onChange={() => toggleInclude(option)}
              />
              <label className='form-check-label' htmlFor={`include-${index}`}>
                {option}
              </label>
            </div>
          </div>
        ))}
      </div>
      {tourIncludes.length > 0 && (
        <div className='mt-3'>
          <small className='text-muted'>
            {i18n.t('hotel_tour.selected')}: {tourIncludes.join(', ')}
          </small>
        </div>
      )}
    </div>
  )

  const renderImageTab = () => (
    <div className='tab-content'>
      <div className='row'>
        <div className='col-md-6'>
          <Input
            inputAttr={{
              type: 'image',
            }}
            label={i18n.t('hotel_tour.field_image')}
            value={image}
            change={(value: React.BaseSyntheticEvent) => setImage(value.target.files[0])}
            smalltext={i18n.t('hotel_tour.field_image_hint')}
          />
        </div>
        {image && typeof image !== 'string' && (
          <div className='col-md-6'>
            <label className='form-label'>{i18n.t('hotel_tour.image_preview')}</label>
            <div className='border rounded p-3'>
              <img
                src={URL.createObjectURL(image)}
                alt='Preview'
                style={{maxWidth: '100%', maxHeight: '200px', objectFit: 'cover'}}
              />
            </div>
          </div>
        )}
        {image && typeof image === 'string' && (
          <div className='col-md-6'>
            <label className='form-label'>{i18n.t('hotel_tour.current_image')}</label>
            <div className='border rounded p-3'>
              <img
                src={image}
                alt='Imagem atual'
                style={{maxWidth: '100%', maxHeight: '200px', objectFit: 'cover'}}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>
                  {i18n.t('hotel_tour.edit_title')}
                </span>
                <BreadCrumbs />
                <span className='text-muted mt-1 fw-bold fs-7'>
                  {i18n.t('hotel_tour.update_info')}
                </span>
              </h3>
              <div className='card-toolbar'>
                <Button
                  text={i18n.t(`buttons.back`)}
                  color='info'
                  iconColor='#FFF'
                  icon='fas fa-arrow-left'
                  size='small'
                  horizontalPadding={6}
                  btnAttr={{
                    type: 'button',
                  }}
                  click={() => navigate('/hospitality/tours')}
                  btnClass='mb-5 fw-bolder me-3'
                />
              </div>
            </div>
            <div className='card-body'>
              {/* Navigation tabs */}
              <HospitalityTabs
                activeTab={activeTab}
                onTabChange={setActiveTab}
                tabs={[
                  {
                    id: 'basic',
                    label: i18n.t('hotel_tour.tab_basic'),
                    icon: 'fas fa-info-circle',
                    content: renderBasicTab(),
                    hasError: false,
                  },
                  {
                    id: 'details',
                    label: i18n.t('hotel_tour.tab_details'),
                    icon: 'fas fa-cog',
                    content: renderDetailsTab(),
                    hasError: false,
                  },
                  {
                    id: 'schedules',
                    label: i18n.t('hotel_tour.tab_schedules'),
                    icon: 'fas fa-clock',
                    content: renderSchedulesTab(),
                    hasError: false,
                  },
                  {
                    id: 'includes',
                    label: i18n.t('hotel_tour.tab_includes'),
                    icon: 'fas fa-list',
                    content: renderIncludesTab(),
                    hasError: false,
                  },
                  {
                    id: 'image',
                    label: i18n.t('hotel_tour.tab_image'),
                    icon: 'fas fa-image',
                    content: renderImageTab(),
                    hasError: false,
                  },
                ]}
              />

              <form>
                {/* Navigation and Submit buttons */}
                <div className='d-flex justify-content-between mt-5'>
                  <div>
                    {activeTab !== 'basic' && (
                      <button
                        type='button'
                        className='btn btn-sm btn-secondary me-3'
                        onClick={() => {
                          const tabs = ['basic', 'details', 'schedules', 'includes', 'image']
                          const currentIndex = tabs.indexOf(activeTab)
                          if (currentIndex > 0) {
                            setActiveTab(tabs[currentIndex - 1])
                          }
                        }}
                      >
                        <i className='fas fa-arrow-left me-2'></i>
                        {i18n.t('buttons.previous')}
                      </button>
                    )}
                    {activeTab !== 'image' && (
                      <button
                        type='button'
                        className='btn btn-sm btn-info'
                        onClick={() => {
                          const tabs = ['basic', 'details', 'schedules', 'includes', 'image']
                          const currentIndex = tabs.indexOf(activeTab)
                          if (currentIndex < tabs.length - 1) {
                            setActiveTab(tabs[currentIndex + 1])
                          }
                        }}
                      >
                        {i18n.t('buttons.next')}
                        <i className='fas fa-arrow-right ms-2'></i>
                      </button>
                    )}
                  </div>
                  <div>
                    <Button
                      text={i18n.t(`buttons.cancel`)}
                      color='danger'
                      size='small'
                      horizontalPadding={8}
                      btnAttr={{
                        type: 'button',
                      }}
                      click={() => navigate('/hospitality/tours')}
                      btnClass='me-3'
                    />
                    <Button
                      text={i18n.t(`general.edit`)}
                      color='primary'
                      iconColor='#FFF'
                      icon='fas fa-save'
                      size='small'
                      horizontalPadding={8}
                      btnAttr={{
                        type: 'button',
                      }}
                      click={updateTour}
                    />
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

const EditHotelTour: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: i18n.t('general.dashboard'), path: 'dashboard', isActive: false},
          {title: i18n.t('hotel_tour.list_title'), path: 'hotel-tours', isActive: false},
          {title: i18n.t('hotel_tour.edit_title'), path: 'hotel-tours/editar', isActive: true},
        ]}
      >
        {i18n.t('hotel_tour.edit_title')}
      </PageTitle>
      <EditHotelTourContent />
    </>
  )
}

export {EditHotelTour}
